import { BillProcessingStatus, ICustomerUtilityBill } from '../customer-utility-bill/interface';

// A file's processing status shares the bill status values (same backend lifecycle) minus NEEDS_REVIEW, which
// only applies to individual bills. Typed as a subset so BillStatusTag / PROCESSING_STATUS_LABEL work as-is.
// (Mirrors the backend CustomerUtilityBillFileProcessingStatus.)
export type CustomerUtilityBillFileStatus = Exclude<BillProcessingStatus, BillProcessingStatus.ParsingNeedsReview>;

export const FILE_PROCESSING_STATUSES: CustomerUtilityBillFileStatus[] = [
    BillProcessingStatus.Pending,
    BillProcessingStatus.DownloadSuccess,
    BillProcessingStatus.DownloadFailed,
    BillProcessingStatus.ParsingInProgress,
    BillProcessingStatus.ParsingSuccess,
    BillProcessingStatus.ParsingFailed,
    BillProcessingStatus.ReparseQueued,
];

export enum CustomerUtilityBillSource {
    Capturis = 'capturis',
    Salesforce = 'salesforce',
}

export const BILL_SOURCE_LABEL: Record<CustomerUtilityBillSource, string> = {
    [CustomerUtilityBillSource.Capturis]: 'Capturis',
    [CustomerUtilityBillSource.Salesforce]: 'Salesforce',
};

export const billSourceLabel = (source: string): string => BILL_SOURCE_LABEL[source as CustomerUtilityBillSource] ?? source;

// customerName is stored normalized (e.g. "dollar-tree"); display it as "Dollar Tree". Display-only — filters and
// requests keep using the raw value.
export const formatCustomerName = (customerName: string): string =>
    customerName
        .replace(/-/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

// File ids are composite ("{source}:{id}"); display only the id part. Display-only — requests keep the full id.
export const formatFileId = (fileId: string): string => fileId.slice(fileId.indexOf(':') + 1);

export interface ICustomerUtilityBillSource {
    id: string;
    source: CustomerUtilityBillSource;
    customerName: string;
}

// One row of the source file (mirrors the backend ICustomerUtilityBillLineItem). Bills are created from these rows;
// a non-null value here is the source of truth for the matching bill field.
export interface ICustomerUtilityBillLineItem {
    siteNumber: string | null;
    siteName: string | null;
    address: string | null;
    city: string | null;
    state: string | null;
    zipCode: string | null;
    market: string | null;
    zone: string | null;
    utility: string | null;
    vendorName: string | null;
    accountNumber: string | null;
    enrollmentId: string | null;
    meterNumber: string | null;
    controlProviderName: string | null;
    rateClass: string | null;
    billingKw: number | null;
    billingKwh: number | null;
    billingPeriodStart: string | null;
    billingPeriodEnd: string | null;
    billLink: string | null;
}

// Mirrors the backend CustomerUtilityBillFileDto. `source` / `bills` are populated only when requested via ?include.
export interface ICustomerUtilityBillFile {
    id: string; // "{source}:{id}"
    sourceId: string;
    processingStatus: CustomerUtilityBillFileStatus;
    processingError: string | null;
    downloadedAt: string | null;
    parsedAt: string | null;
    createdAt: string;
    updatedAt: string;
    source?: ICustomerUtilityBillSource;
    bills?: ICustomerUtilityBill[];
    meta?: ICustomerUtilityBillLineItem[];
}

// Mirrors the backend's reparse eligibility gate for files.
export const REPARSABLE_FILE_STATUSES: CustomerUtilityBillFileStatus[] = [
    BillProcessingStatus.DownloadSuccess,
    BillProcessingStatus.ParsingSuccess,
    BillProcessingStatus.ParsingFailed,
    BillProcessingStatus.ReparseQueued,
];

export const isFileReparsable = (file: ICustomerUtilityBillFile): boolean => REPARSABLE_FILE_STATUSES.includes(file.processingStatus);

// Non-terminal states while a (re-)parse is in flight — used to know when to stop polling after a batch reparse.
const FILE_REPARSE_IN_PROGRESS_STATUSES: CustomerUtilityBillFileStatus[] = [
    BillProcessingStatus.ReparseQueued,
    BillProcessingStatus.ParsingInProgress,
];

export const isFileReparseInProgress = (file: ICustomerUtilityBillFile): boolean => FILE_REPARSE_IN_PROGRESS_STATUSES.includes(file.processingStatus);
